import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faBox,
	faBuilding,
	faBullseye,
	faClipboardList,
	faFileInvoice,
	faFileInvoiceDollar,
	faIdCard,
	faLink,
	faLinkSlash,
	faShoppingCart,
	faSnowflake,
	faSpinner,
	faTruck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	ApiService,
	formatCurrency,
	IssueQueryService,
	type LinkedEntityInfo,
} from '@supply-mover-2-0/core';
import { LinkableEntityType } from '@supply-mover-2-0/shared';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import { Text } from '../base/Text';
import { Button } from '../forms/Button';
import { SingleSelect } from '../forms/SingleSelect';

export interface LinkedRecord {
	entityType: LinkableEntityType;
	entityId: number;
	entityName: string;
	entitySecondaryInfo?: string;
}

interface LinkedRecordsEditorProps {
	// For create mode - controlled state
	value?: Array<LinkedRecord>;
	onChange?: (links: Array<LinkedRecord>) => void;

	// For edit mode - server-side management
	issueId?: number;
	onLinkCreated?: () => void;
	onLinkRemoved?: () => void;

	// Optional label override
	label?: string;
}

interface EntityTypeConfig {
	type: LinkableEntityType;
	icon: IconDefinition;
	color: string;
	label: string;
	searchEndpoint: string;
	searchParam: string;
	nameField: string;
	secondaryFields?: Array<string>;
}

const entityTypeConfigs: Array<EntityTypeConfig> = [
	{
		type: LinkableEntityType.CLIENT,
		icon: faBuilding,
		color: '#3B82F6',
		label: 'Client',
		searchEndpoint: '/clients',
		searchParam: 'search',
		nameField: 'name',
		secondaryFields: ['city', 'state'],
	},
	{
		type: LinkableEntityType.VENDOR,
		icon: faTruck,
		color: '#06B6D4',
		label: 'Vendor',
		searchEndpoint: '/vendors',
		searchParam: 'keywords',
		nameField: 'name',
		secondaryFields: ['city', 'state'],
	},
	{
		type: LinkableEntityType.OPPORTUNITY,
		icon: faBullseye,
		color: '#F59E0B',
		label: 'Opportunity',
		searchEndpoint: '/opportunities',
		searchParam: 'search',
		nameField: 'mSmName',
		secondaryFields: ['stage'],
	},
	{
		type: LinkableEntityType.ORDER,
		icon: faShoppingCart,
		color: '#10B981',
		label: 'Order',
		searchEndpoint: '/orders',
		searchParam: 'orderKeywords',
		nameField: 'orderNumber',
		secondaryFields: ['total'],
	},
	{
		type: LinkableEntityType.QUOTE,
		icon: faFileInvoiceDollar,
		color: '#06B6D4',
		label: 'Quote',
		searchEndpoint: '/quotes',
		searchParam: 'search',
		nameField: 'quoteNumber',
		secondaryFields: ['total'],
	},
	{
		type: LinkableEntityType.INVOICE,
		icon: faFileInvoice,
		color: '#10B981',
		label: 'Invoice',
		searchEndpoint: '/invoices',
		searchParam: 'invoiceKeywords',
		nameField: 'invoiceNumber',
		secondaryFields: ['total'],
	},
	{
		type: LinkableEntityType.ITEM,
		icon: faBox,
		color: '#6B7280',
		label: 'Item',
		searchEndpoint: '/items',
		searchParam: 'search',
		nameField: 'name',
		secondaryFields: ['sku', 'manufacturer'],
	},
	{
		type: LinkableEntityType.SOURCING_REQUEST,
		icon: faClipboardList,
		color: '#3B82F6',
		label: 'Sourcing Request',
		searchEndpoint: '/sourcing-requests',
		searchParam: 'search',
		nameField: 'requestNumber',
		secondaryFields: ['status'],
	},
	{
		type: LinkableEntityType.LICENSE,
		icon: faIdCard,
		color: '#8B5CF6',
		label: 'License',
		searchEndpoint: '/licenses',
		searchParam: 'search',
		nameField: 'licenseNumber',
		secondaryFields: ['type', 'status'],
	},
	{
		type: LinkableEntityType.VEHICLE,
		icon: faTruck,
		color: '#0EA5E9',
		label: 'Vehicle',
		searchEndpoint: '/vehicles',
		searchParam: 'search',
		nameField: 'name',
		secondaryFields: ['unitNumber', 'licensePlate'],
	},
	{
		type: LinkableEntityType.MERCHANDISER,
		icon: faSnowflake,
		color: '#14B8A6',
		label: 'Merchandiser',
		searchEndpoint: '/merchandisers',
		searchParam: 'search',
		nameField: 'name',
		secondaryFields: ['manufacturer', 'model'],
	},
];

function getEntityConfig(entityType: string): EntityTypeConfig | undefined {
	return entityTypeConfigs.find((c) => (c.type as string) === entityType);
}

interface SearchResult {
	id: number;
	name: string;
	secondaryInfo?: string;
}

export function LinkedRecordsEditor({
	value,
	onChange,
	issueId,
	onLinkCreated,
	onLinkRemoved,
	label,
}: LinkedRecordsEditorProps): ReactNode {
	const { t } = useTranslation();
	const colors = useThemeColors();

	// Server-loaded links (for edit mode)
	const [serverLinks, setServerLinks] = useState<Array<LinkedEntityInfo>>([]);
	const [isLoadingLinks, setIsLoadingLinks] = useState(false);

	// Removing state
	const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
	const [confirmingUnlinkKey, setConfirmingUnlinkKey] = useState<string | null>(
		null,
	);

	// Add form state (always visible)
	const [addEntityType, setAddEntityType] = useState<LinkableEntityType | ''>(
		'',
	);
	const [addSearchTerm, setAddSearchTerm] = useState('');
	const [addSearchResults, setAddSearchResults] = useState<Array<SearchResult>>(
		[],
	);
	const [addIsSearching, setAddIsSearching] = useState(false);
	const [addIsLinking, setAddIsLinking] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const searchInputRef = useRef<HTMLInputElement>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Track current value in a ref to avoid recreating callbacks when value changes
	const valueRef = useRef(value);
	useEffect(() => {
		valueRef.current = value;
	}, [value]);

	// Determine if we're in controlled (create) or uncontrolled (edit) mode
	const isEditMode = issueId != null;

	// Get the current list of links
	const currentLinks: Array<LinkedRecord> = useMemo(() => {
		if (isEditMode) {
			return serverLinks.map((l) => ({
				entityType: l.entityType as LinkableEntityType,
				entityId: l.entityId,
				entityName: l.entityName ?? '',
				entitySecondaryInfo: l.entitySecondaryInfo,
			}));
		}

		return value ?? [];
	}, [isEditMode, serverLinks, value]);

	// Load links from server in edit mode
	const loadServerLinks = useCallback(async (): Promise<void> => {
		if (issueId == null) {
			return;
		}

		setIsLoadingLinks(true);

		try {
			const result = await IssueQueryService.getIssueLinksEnriched(issueId, {
				pageSize: 100,
			});

			if (result.success) {
				setServerLinks(result.data ?? []);
			}
		} catch (_err) {
			console.debug('Error loading links');
		} finally {
			setIsLoadingLinks(false);
		}
	}, [issueId]);

	useEffect(() => {
		if (isEditMode) {
			void loadServerLinks();
		}
	}, [isEditMode, loadServerLinks]);

	// Get config for current add form entity type
	const addConfig = useMemo(
		() => (addEntityType !== '' ? getEntityConfig(addEntityType) : undefined),
		[addEntityType],
	);

	// Handle entity type change
	const handleTypeChange = useCallback((newType: string) => {
		setAddEntityType(newType as LinkableEntityType | '');
		setAddSearchTerm('');
		setAddSearchResults([]);
		setHighlightedIndex(-1);

		// Focus search input after type change
		setTimeout(() => {
			searchInputRef.current?.focus();
		}, 0);
	}, []);

	// Search entities
	const searchEntities = useCallback(
		async (searchTerm: string) => {
			if (addEntityType === '') {
				return;
			}

			const config = getEntityConfig(addEntityType);

			if (config == null) {
				return;
			}

			setAddIsSearching(true);

			try {
				const filters = { [config.searchParam]: searchTerm };
				const response = await ApiService.apiGet<{
					success: boolean;
					data?: Array<Record<string, unknown>>;
					error?: string;
				}>(
					`${config.searchEndpoint}?filters=${encodeURIComponent(JSON.stringify(filters))}&page=1&limit=10`,
				);

				if (response.success && response.data != null) {
					// Filter out already linked entities
					const existingIds = currentLinks
						.filter((l) => l.entityType === addEntityType)
						.map((l) => l.entityId);

					const extractId = (
						item: Record<string, unknown>,
					): number | undefined => {
						const idValue = item['id'] ?? item['_id'];

						if (typeof idValue === 'number') {
							return idValue;
						}

						return undefined;
					};

					const mapped = response.data
						.map((item) => {
							const id = extractId(item);

							if (id == null || existingIds.includes(id)) {
								return null;
							}

							const nameValue = item[config.nameField];
							const name = typeof nameValue === 'string' ? nameValue : `#${id}`;

							let secondaryInfo: string | undefined;

							if (config.secondaryFields != null) {
								const parts = config.secondaryFields
									.map((field) => {
										const fieldValue = item[field];

										if (typeof fieldValue === 'number') {
											if (field === 'total') {
												return formatCurrency(fieldValue);
											}

											return String(fieldValue);
										}

										if (
											typeof fieldValue === 'string' &&
											fieldValue.length > 0
										) {
											return fieldValue;
										}

										return null;
									})
									.filter((v): v is string => v != null);

								if (parts.length > 0) {
									secondaryInfo = parts.join(', ');
								}
							}

							return { id, name, secondaryInfo };
						})
						.filter((item): item is SearchResult => item != null);

					setAddSearchResults(mapped);
					setHighlightedIndex(mapped.length > 0 ? 0 : -1);
				} else {
					setAddSearchResults([]);
					setHighlightedIndex(-1);
				}
			} catch (_err) {
				console.debug('Search error');
				setAddSearchResults([]);
			} finally {
				setAddIsSearching(false);
			}
		},
		[addEntityType, currentLinks],
	);

	// Handle search input change with debounce
	const handleSearchChange = useCallback(
		(inputValue: string) => {
			setAddSearchTerm(inputValue);
			setAddSearchResults([]);
			setHighlightedIndex(-1);

			if (searchTimeoutRef.current != null) {
				clearTimeout(searchTimeoutRef.current);
			}

			if (inputValue.length >= 1) {
				searchTimeoutRef.current = setTimeout(() => {
					void searchEntities(inputValue);
				}, 300);
			}
		},
		[searchEntities],
	);

	// Select an entity from search results
	const handleSelectEntity = useCallback(
		async (entity: SearchResult) => {
			if (addEntityType === '') {
				return;
			}

			if (isEditMode) {
				// Server-side linking
				setAddIsLinking(true);

				try {
					const result = await IssueQueryService.linkEntityToIssue(
						issueId,
						addEntityType,
						entity.id,
						entity.name,
					);

					if (result.success) {
						Toast.show({
							type: 'success',
							text1: t('Record linked successfully'),
						});

						// Optimistically add link to local state
						setServerLinks((prev) => [
							...prev,
							{
								entityType: addEntityType,
								entityId: entity.id,
								entityName: entity.name,
								entitySecondaryInfo: entity.secondaryInfo,
							} as LinkedEntityInfo,
						]);

						onLinkCreated?.();

						// Clear search but keep entity type, refocus input
						setAddSearchTerm('');
						setAddSearchResults([]);
						setHighlightedIndex(-1);
						setTimeout(() => {
							searchInputRef.current?.focus();
						}, 0);
					} else {
						Toast.show({
							type: 'error',
							text1: result.error ?? t('Failed to link record'),
						});
					}
				} catch (_err) {
					Toast.show({
						type: 'error',
						text1: t('An error occurred while linking record'),
					});
				} finally {
					setAddIsLinking(false);
				}
			} else {
				// Local state (create mode)
				const newLink: LinkedRecord = {
					entityType: addEntityType,
					entityId: entity.id,
					entityName: entity.name,
					entitySecondaryInfo: entity.secondaryInfo,
				};
				onChange?.([...(valueRef.current ?? []), newLink]);

				// Clear search but keep entity type, refocus input
				setAddSearchTerm('');
				setAddSearchResults([]);
				setHighlightedIndex(-1);
				setTimeout(() => {
					searchInputRef.current?.focus();
				}, 0);
			}
		},
		[addEntityType, isEditMode, issueId, onChange, onLinkCreated, t],
	);

	// Keyboard navigation handler
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (addSearchResults.length === 0) {
				return;
			}

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setHighlightedIndex((prev) =>
					prev < addSearchResults.length - 1 ? prev + 1 : prev,
				);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
			} else if (e.key === 'Enter' && highlightedIndex >= 0) {
				e.preventDefault();
				const selectedResult = addSearchResults[highlightedIndex];

				if (selectedResult != null) {
					void handleSelectEntity(selectedResult);
				}
			}
		},
		[addSearchResults, highlightedIndex, handleSelectEntity],
	);

	// Remove a linked record
	const handleConfirmUnlink = useCallback(
		async (link: LinkedRecord) => {
			const linkKey = `${link.entityType}-${link.entityId}`;
			setConfirmingUnlinkKey(null);

			if (isEditMode) {
				// Server-side unlinking
				setRemovingIds((prev) => new Set(prev).add(linkKey));

				try {
					const result = await IssueQueryService.unlinkEntityFromIssue(
						issueId,
						link.entityType,
						link.entityId,
						link.entityName,
					);

					if (result.success) {
						Toast.show({
							type: 'success',
							text1: t('Record unlinked successfully'),
						});

						// Optimistically remove link from local state
						setServerLinks((prev) =>
							prev.filter(
								(l) =>
									!(
										l.entityType === link.entityType &&
										l.entityId === link.entityId
									),
							),
						);

						onLinkRemoved?.();
					} else {
						Toast.show({
							type: 'error',
							text1: result.error ?? t('Failed to unlink record'),
						});
					}
				} catch (_err) {
					Toast.show({
						type: 'error',
						text1: t('An error occurred while unlinking record'),
					});
				} finally {
					setRemovingIds((prev) => {
						const next = new Set(prev);
						next.delete(linkKey);

						return next;
					});
				}
			} else {
				// Local state (create mode)
				onChange?.(
					(valueRef.current ?? []).filter(
						(l) =>
							!(
								l.entityType === link.entityType && l.entityId === link.entityId
							),
					),
				);
			}
		},
		[isEditMode, issueId, onChange, onLinkRemoved, t],
	);

	// Entity type options for dropdown
	const typeOptions = useMemo(
		() =>
			entityTypeConfigs.map((config) => ({
				value: config.type,
				label: t(config.label),
			})),
		[t],
	);

	if (isLoadingLinks) {
		return (
			<div className="space-y-2">
				{label != null && (
					<Text
						className="text-sm font-medium"
						style={{ color: colors.values.textPrimary }}
					>
						{t(label)}
					</Text>
				)}
				<div
					className="flex items-center justify-center py-4"
					style={{ color: colors.values.textSecondary }}
				>
					<FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
					{t('Loading...')}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{label != null && (
				<Text
					className="text-sm font-medium"
					style={{ color: colors.values.textPrimary }}
				>
					{t(label)}
				</Text>
			)}

			{/* Existing links */}
			{currentLinks.length > 0 && (
				<div
					className="border rounded-lg divide-y divide-gray-200 dark:divide-gray-700"
					style={{
						borderColor: colors.values.borderLight,
						backgroundColor: colors.values.backgroundPrimary as string,
					}}
				>
					{currentLinks.map((link) => {
						const config = getEntityConfig(link.entityType);
						const linkKey = `${link.entityType}-${link.entityId}`;
						const isRemoving = removingIds.has(linkKey);

						return (
							<div
								key={linkKey}
								className="flex items-center gap-3 p-3"
								style={{
									opacity: isRemoving ? 0.5 : 1,
								}}
							>
								{/* Type badge with icon */}
								<div
									className="flex items-center gap-2 min-w-[120px]"
									style={{
										color: config?.color ?? colors.values.textSecondary,
									}}
								>
									<FontAwesomeIcon
										icon={config?.icon ?? faLink}
										className="text-sm"
									/>
									<span className="text-sm">
										{t(config?.label ?? link.entityType)}
									</span>
								</div>

								{/* Entity name */}
								<div className="flex-1 min-w-0">
									<Text
										className="text-sm truncate block"
										style={{ color: colors.values.textPrimary }}
									>
										{t(link.entityName)}
									</Text>
									{link.entitySecondaryInfo != null && (
										<Text
											className="text-xs truncate block"
											style={{ color: colors.values.textSecondary }}
										>
											{t(link.entitySecondaryInfo)}
										</Text>
									)}
								</div>

								{/* Remove button */}
								{confirmingUnlinkKey === linkKey ? (
									<div className="flex items-center gap-2">
										<Text
											className="text-sm"
											style={{ color: colors.values.textSecondary }}
										>
											{t('Unlink?')}
										</Text>
										<Button
											variant="danger"
											size="sm"
											onClick={() => {
												void handleConfirmUnlink(link);
											}}
											disabled={isRemoving}
										>
											{isRemoving ? (
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin"
												/>
											) : (
												t('Yes')
											)}
										</Button>
										<Button
											variant="secondary"
											size="sm"
											onClick={() => {
												setConfirmingUnlinkKey(null);
											}}
											disabled={isRemoving}
										>
											{t('No')}
										</Button>
									</div>
								) : (
									<button
										type="button"
										onClick={() => {
											setConfirmingUnlinkKey(linkKey);
										}}
										disabled={isRemoving}
										className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
										title={t('Unlink')}
									>
										<FontAwesomeIcon
											icon={faLinkSlash}
											className="text-sm"
											style={{ color: colors.values.textSecondary }}
										/>
									</button>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Add New Link form (always visible) */}
			<div>
				<Text
					className="text-sm font-medium mb-2 block"
					style={{ color: colors.values.textPrimary }}
				>
					{t('Add New Link')}
				</Text>
				<div
					className="border rounded-lg p-3 space-y-2 border-gray-200 dark:border-gray-700"
					style={{
						backgroundColor: colors.values.backgroundSecondary as string,
					}}
				>
					<div className="flex items-center gap-2">
						{/* Type dropdown */}
						<div className="w-[160px]">
							<SingleSelect
								name="entity-type"
								value={addEntityType}
								onChange={handleTypeChange}
								options={typeOptions}
								placeholder={t('Select type...')}
								disabled={addIsLinking}
							/>
						</div>

						{/* Search input */}
						<div className="flex-1 relative">
							<input
								ref={searchInputRef}
								type="text"
								value={addSearchTerm}
								onChange={(e) => {
									handleSearchChange(e.target.value);
								}}
								onKeyDown={handleKeyDown}
								placeholder={
									addEntityType !== ''
										? t(
												`Search ${addConfig?.label.toLowerCase() ?? 'record'}s...`,
											)
										: t('Select type first')
								}
								disabled={addEntityType === '' || addIsLinking}
								className="w-full px-3 py-2 border rounded-lg text-sm"
								style={{
									borderColor: colors.values.border as string,
									backgroundColor: colors.values.backgroundPrimary as string,
									color: colors.values.textPrimary,
								}}
							/>
							{addIsSearching && (
								<div className="absolute inset-y-0 right-0 flex items-center pr-3">
									<FontAwesomeIcon
										icon={faSpinner}
										className="animate-spin text-sm"
										style={{ color: colors.values.textSecondary }}
									/>
								</div>
							)}
						</div>
					</div>

					{/* Search results dropdown */}
					{addSearchResults.length > 0 && (
						<div
							className="border rounded-lg max-h-[200px] overflow-y-auto border-gray-200 dark:border-gray-700"
							style={{
								backgroundColor: colors.values.backgroundPrimary as string,
							}}
						>
							{addSearchResults.map((entity, index) => (
								<button
									key={entity.id}
									type="button"
									onClick={() => {
										void handleSelectEntity(entity);
									}}
									disabled={addIsLinking}
									className={`w-full text-left p-3 transition-colors border-b last:border-b-0 border-gray-200 dark:border-gray-700 ${
										index === highlightedIndex
											? 'bg-gray-100 dark:bg-gray-700'
											: 'hover:bg-gray-50 dark:hover:bg-gray-800'
									}`}
								>
									<div className="flex items-center gap-2">
										{addConfig != null && (
											<FontAwesomeIcon
												icon={addConfig.icon}
												className="text-sm"
												style={{ color: addConfig.color }}
											/>
										)}
										<div className="flex-1 min-w-0">
											<Text
												className="text-sm font-medium truncate block"
												style={{ color: colors.values.textPrimary }}
											>
												{t(entity.name)}
											</Text>
											{entity.secondaryInfo != null && (
												<Text
													className="text-xs truncate block"
													style={{ color: colors.values.textSecondary }}
												>
													{t(entity.secondaryInfo)}
												</Text>
											)}
										</div>
									</div>
								</button>
							))}
						</div>
					)}

					{/* No results message */}
					{addSearchTerm.length > 0 &&
						!addIsSearching &&
						addSearchResults.length === 0 && (
							<Text
								className="text-sm py-2 text-center"
								style={{ color: colors.values.textSecondary }}
							>
								{t('No results found')}
							</Text>
						)}

					{/* Linking indicator */}
					{addIsLinking && (
						<div
							className="flex items-center justify-center py-2"
							style={{ color: colors.values.textSecondary }}
						>
							<FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
							{t('Linking...')}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default LinkedRecordsEditor;
